import { C } from "../lib/theme.js";
import { metricMeta } from "../lib/compute.js";

// Benchmarks / metric weights / comparison basis. Lifted verbatim from the
// original inline AdminPanel — the ownership grid has moved to the
// Properties tab so this view focuses on scoring knobs only.
export default function ManageScoring({
  ttmAvailable,
  basis,
  setBasis,
  benchmarks,
  setBenchmarks,
  weights,
  setWeights,
  onSave,
  saving,
  saveStatus,
}) {
  const updateB = (k, v) => setBenchmarks((prev) => ({ ...prev, [k]: parseFloat(v) || 0 }));
  const updateW = (k, v) => setWeights((prev) => ({ ...prev, [k]: parseInt(v) || 0 }));
  const totalW = Object.values(weights).reduce((s, w) => s + w, 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12 }}>
        <div style={{ fontSize: 12, color: C.sub }}>
          Score ranges: <b>80–100</b> Strong · <b>65–79</b> Watch · <b>45–64</b> Concern · <b>0–44</b> Action Needed
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {saveStatus && <span style={{ fontSize: 11, color: C.sub }}>{saveStatus}</span>}
          {onSave && (
            <button
              onClick={onSave}
              disabled={saving}
              style={{
                background: C.accent,
                color: "#fff",
                border: "none",
                borderRadius: 5,
                padding: "6px 14px",
                fontSize: 12,
                fontWeight: 600,
                cursor: saving ? "wait" : "pointer",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 }}>Comparison Basis</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            { id: "benchmark", l: "User Benchmarks" },
            { id: "trailing12", l: "Trailing 12 Mo" },
            { id: "budget", l: "Budget" },
          ].map((b) => {
            const disabled = b.id === "trailing12" && !ttmAvailable;
            return (
              <button
                key={b.id}
                onClick={() => !disabled && setBasis(b.id)}
                style={{
                  background: basis === b.id ? C.accent : "transparent",
                  color: basis === b.id ? "#fff" : C.sub,
                  border: `1px solid ${basis === b.id ? C.accent : C.border}`,
                  padding: "6px 14px",
                  borderRadius: 5,
                  fontSize: 11,
                  fontWeight: 500,
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                {b.l}
                {disabled ? " (needs data)" : ""}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 }}>Benchmark Targets</div>
          <div style={{ fontSize: 11, color: C.dim, marginBottom: 8 }}>
            Set portfolio-wide targets. Score of 80 = meeting target.
          </div>
          {Object.entries(metricMeta).map(([key, meta]) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: C.sub, width: 110 }}>{meta.label}</span>
              <input
                type="number"
                value={benchmarks[key]}
                onChange={(e) => updateB(key, e.target.value)}
                style={{
                  width: 70,
                  padding: "4px 8px",
                  border: `1px solid ${C.border}`,
                  borderRadius: 4,
                  fontSize: 12,
                  fontFamily: "'DM Mono',monospace",
                  background: basis === "benchmark" ? "#fff" : C.borderLight,
                }}
                disabled={basis !== "benchmark"}
              />
              <span style={{ fontSize: 11, color: C.dim }}>{meta.unit}</span>
            </div>
          ))}
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 }}>Metric Weights</div>
          <div style={{ fontSize: 11, color: C.dim, marginBottom: 8 }}>
            Total: {totalW}{" "}
            {totalW !== 100 && <span style={{ color: C.amber }}>(will normalize to 100%)</span>}
          </div>
          {Object.entries(metricMeta).map(([key, meta]) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: C.sub, width: 110 }}>{meta.label}</span>
              <input
                type="number"
                value={weights[key]}
                onChange={(e) => updateW(key, e.target.value)}
                min="0"
                max="100"
                style={{
                  width: 50,
                  padding: "4px 8px",
                  border: `1px solid ${C.border}`,
                  borderRadius: 4,
                  fontSize: 12,
                  fontFamily: "'DM Mono',monospace",
                }}
              />
              <div style={{ flex: 1, height: 4, background: C.borderLight, borderRadius: 2, overflow: "hidden" }}>
                <div
                  style={{
                    width: `${totalW ? (weights[key] / totalW) * 100 : 0}%`,
                    height: "100%",
                    background: C.accentSoft,
                    borderRadius: 2,
                  }}
                />
              </div>
              <span style={{ fontSize: 11, color: C.dim, width: 30, textAlign: "right" }}>
                {totalW ? Math.round((weights[key] / totalW) * 100) : 0}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
